import { ConditionInterpreter, TokenType, Expression, Char } from "@evergis/condition";
import { AttributeType } from "@evergis/api";
import { ComparisonOperator, RangeValue } from "@evergis/ui";

import { ClassificationClass, ClassificationType, isRangeClass } from "../../../interfaces/classification";
import { OPERATOR_CONDITION_REMAP } from "./ComparisonOperator";
import { FilterConditionOperation } from "../../../components/LayerFilter/types";

type ConditionOptions = {
  fromInclusive?: boolean;
  toInclusive?: boolean;
};

const NULL_EXPRESSION = {
  attribute: null,
  operation: null,
  value: null,
};

export class ClassificationCondition {
  private readonly condition: string;

  private readonly expressions: Expression[] = [];

  constructor(condition: string) {
    this.condition = condition;
    const conditionResult = new ConditionInterpreter(condition).value;

    this.expressions = conditionResult instanceof Error ? [] : conditionResult.expressions;
  }

  static from(
    classificationClass: ClassificationClass,
    options: ConditionOptions = { fromInclusive: false, toInclusive: true },
  ) {
    const { attribute } = classificationClass;

    if (isRangeClass(classificationClass)) {
      const { from, to, attribute: rangeAttribute } = classificationClass;

      const condition = [];

      if (from !== null) {
        const operand = options.fromInclusive ? ComparisonOperator.GreaterOrEquals : ComparisonOperator.GreaterThan;

        condition.push(
          `(${rangeAttribute.name} ${OPERATOR_CONDITION_REMAP[operand]} ${formatValue(from, attribute.type)})`,
        );
      }

      if (to !== null) {
        const operand = options.toInclusive ? ComparisonOperator.LessOrEquals : ComparisonOperator.LessThan;

        condition.push(
          `(${rangeAttribute.name} ${OPERATOR_CONDITION_REMAP[operand]} ${formatValue(to, attribute.type)})`,
        );
      }

      return new ClassificationCondition(condition.join(` ${FilterConditionOperation.AND} `));
    }

    return new ClassificationCondition(
      `${attribute.name} == ${formatValue(classificationClass.uniqueValue, attribute.type)}`,
    );
  }

  public get attributeName() {
    return this.leftExpression?.attribute;
  }

  public get uniqueValue() {
    if (this.isUnique()) {
      const { value } = this.leftExpression;

      return typeof value === "string" ? value : value.toString();
    }

    console.warn(`Trying to get unique from range condition ${this.condition}`);

    return "";
  }

  public get range(): RangeValue {
    if (!this.isRange()) {
      return [0, 0];
    }

    if (this.expressions.length === 2) {
      const { value: from } = this.leftExpression;
      const { value: to } = this.rightExpression;

      return [from, to] as RangeValue;
    }

    if (this.expressions.length === 1) {
      const { operation, value } = this.leftExpression;
      const isFrom = operation === TokenType.ArGr;

      return [isFrom ? value : null, isFrom ? null : value] as RangeValue;
    }

    return [0, 0];
  }

  public get type(): ClassificationType {
    return this.isUnique() ? ClassificationType.Unique : ClassificationType.Range;
  }

  public isUnique() {
    return this.leftExpression?.operation === TokenType.ArEq;
  }

  public isRange() {
    return [TokenType.ArGr, TokenType.ArGre, TokenType.ArLs, TokenType.ArLse].includes(this.leftExpression?.operation);
  }

  public toString() {
    return this.condition;
  }

  private get leftExpression(): Expression {
    return this.expressions.length > 0 ? this.expressions[0] : NULL_EXPRESSION;
  }

  private get rightExpression(): Expression {
    return this.expressions.length > 1 ? this.expressions[1] : NULL_EXPRESSION;
  }
}

function formatValue(value: string | number, attributeType: AttributeType) {
  let result = value;

  if (typeof value === "string") {
    result = `"${Char.escapeSpecChars(value)}"`;
  }

  if (attributeType === AttributeType.DateTime) {
    return `#${result}`;
  }

  return result;
}
